import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    Award,
    BadgeCheck,
    BriefcaseBusiness,
    Building2,
    CheckCircle2,
    ExternalLink,
    Globe2,
    Handshake,
    HeartHandshake,
    Link as LinkIcon,
    MapPin,
    ShieldCheck,
    Sparkles,
    Star,
    Target,
    Users,
    Wrench,
} from 'lucide-react';

type PartnerIconComponent = React.ComponentType<{
    className?: string;
    size?: number;
    style?: CSSProperties;
    strokeWidth?: number;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

const partnerIconRegistry = {
    arrowRight: ArrowRight,
    award: Award,
    badgeCheck: BadgeCheck,
    briefcase: BriefcaseBusiness,
    building: Building2,
    checkCircle: CheckCircle2,
    externalLink: ExternalLink,
    globe: Globe2,
    handshake: Handshake,
    heartHandshake: HeartHandshake,
    link: LinkIcon,
    mapPin: MapPin,
    shieldCheck: ShieldCheck,
    sparkles: Sparkles,
    star: Star,
    target: Target,
    users: Users,
    wrench: Wrench,
} satisfies Record<string, PartnerIconComponent>;

export type PartnerIconToken = keyof typeof partnerIconRegistry;

export const partnerIconTokens = Object.keys(partnerIconRegistry) as PartnerIconToken[];

export type PartnerIconRenderer = React.ElementType<{
    className?: string;
    style?: CSSProperties;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

export type PartnerIcon = PartnerIconToken | React.ReactNode | PartnerIconRenderer | false;

export type PartnerLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    style?: CSSProperties;
    itemProp?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type PartnerImageSource =
    | string
    | {
          src?: string;
          width?: number;
          height?: number;
          [key: string]: unknown;
      };

export type PartnerLogo = {
    id?: string | number;
    src?: PartnerImageSource;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
    objectFit?: CSSProperties['objectFit'];
    objectPosition?: string;
    backgroundColor?: string;
    backgroundColour?: string;
};

export type PartnerImage = {
    id?: string | number;
    src?: PartnerImageSource;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    caption?: React.ReactNode;
    description?: React.ReactNode;
    width?: number;
    height?: number;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    objectFit?: CSSProperties['objectFit'];
    objectPosition?: string;
};

export type PartnerChip = React.ReactNode | PartnerChipObject;

export type PartnerChipObject = {
    id?: string | number;
    label: React.ReactNode;
    href?: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    textColor?: string;
    textColour?: string;
    color?: string;
    colour?: string;
};

export type PartnerMetric = {
    id?: string | number;
    value: React.ReactNode;
    label: React.ReactNode;
    description?: React.ReactNode;
    ariaLabel?: string;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string | false;
    linkRel?: string | false;
    sponsored?: boolean;
    nofollow?: boolean;
    ugc?: boolean;
    variant?: 'primary' | 'secondary' | 'text';
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
};

export type PartnerSchemaType =
    | 'Organization'
    | 'LocalBusiness'
    | 'ProfessionalService'
    | 'Corporation'
    | 'Person'
    | (string & {});

export type PartnerColorToken = keyof typeof partnerTokens.colors;

export type PartnerColors = Partial<Record<PartnerColorToken, string>>;

export const partnerTokens = {
    colors: {
        background: '#f6f8f5',
        surface: '#ffffff',
        surfaceSoft: '#eef6f2',
        text: '#111827',
        textSoft: '#344054',
        textMuted: '#667085',
        accent: '#0f766e',
        accentDark: '#115e59',
        accentSoft: 'rgba(15, 118, 110, 0.12)',
        action: '#2563eb',
        actionDark: '#1d4ed8',
        actionSoft: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(17, 24, 39, 0.1)',
        borderStrong: 'rgba(17, 24, 39, 0.16)',
        chipBackground: 'rgba(17, 24, 39, 0.06)',
        logoBackground: '#f8fafc',
        mediaFallback: '#e7edf0',
        star: '#f59e0b',
        shadow: '0 16px 38px rgba(17, 24, 39, 0.08)',
        hoverShadow: '0 22px 52px rgba(17, 24, 39, 0.13)',
    },
    sizes: {
        maxWidth: 1180,
        railTileWidth: 286,
        railTileMinWidth: 246,
        directoryTileMinWidth: 320,
        tileGap: 16,
    },
    typography: {
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },
} as const;

export const DefaultPartnerLink: PartnerLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

export function toPartnerCssSize(value?: number | string) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

export function makePartnerSoftAccent(accent?: string) {
    return accent ? `color-mix(in srgb, ${accent} 12%, transparent)` : undefined;
}

export function getPartnerImageSource(source?: PartnerImageSource) {
    if (!source) return '';
    if (typeof source === 'string') return source;
    return typeof source.src === 'string' ? source.src : '';
}

export function getPartnerLogoSource(logo?: PartnerLogo, logoSrc?: PartnerImageSource) {
    return (
        getPartnerImageSource(logoSrc) ||
        getPartnerImageSource(logo?.src) ||
        logo?.thumbnail ||
        logo?.url ||
        logo?.original ||
        ''
    );
}

export function getPartnerMediaSource(image?: PartnerImage | string) {
    if (!image) return '';
    if (typeof image === 'string') return image;

    return (
        getPartnerImageSource(image.src) ||
        image.thumbnail ||
        image.url ||
        image.original ||
        ''
    );
}

export function getPartnerSourceDimensions(
    source?: PartnerImageSource,
    fallback?: { width?: number; height?: number },
) {
    if (typeof source === 'object' && source) {
        return {
            width: fallback?.width || source.width,
            height: fallback?.height || source.height,
        };
    }

    return fallback || {};
}

export function getReadablePartnerText(node: React.ReactNode, fallback = '') {
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    return fallback;
}

export function getPartnerInitials(name: React.ReactNode, fallback = 'P') {
    const text = getReadablePartnerText(name, fallback).trim();
    const words = text.split(/\s+/).filter(Boolean);
    const initials = words
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');

    return initials || fallback;
}

export function getPartnerLogoAlt({
    logo,
    logoAlt,
    name,
    fallback = 'Partner logo',
}: {
    logo?: PartnerLogo;
    logoAlt?: string;
    name?: React.ReactNode;
    fallback?: string;
}) {
    if (logo?.decorative) return '';

    const partnerName = getReadablePartnerText(name);
    return logoAlt || logo?.alt || logo?.title || (partnerName ? `${partnerName} logo` : fallback);
}

function normaliseRelTokens(value?: string | false) {
    if (!value || value === 'default') return [];

    return value
        .split(/\s+/)
        .map((token) => token.trim())
        .filter(Boolean);
}

export function resolvePartnerLinkProps({
    rel,
    linkRel,
    target,
    sponsored,
    nofollow,
    ugc,
    defaultRel,
    defaultTarget,
}: {
    rel?: string | false;
    linkRel?: string | false;
    target?: string;
    sponsored?: boolean;
    nofollow?: boolean;
    ugc?: boolean;
    defaultRel?: string | false;
    defaultTarget?: string;
}) {
    const resolvedTarget = target || defaultTarget;
    const explicitRel = linkRel !== undefined ? linkRel : rel;
    const relValue = explicitRel === undefined || explicitRel === 'default' ? defaultRel : explicitRel;
    const relTokens = new Set<string>();

    if (relValue !== false) {
        normaliseRelTokens(relValue).forEach((token) => relTokens.add(token));

        if (sponsored) relTokens.add('sponsored');
        if (nofollow) relTokens.add('nofollow');
        if (ugc) relTokens.add('ugc');
        if (resolvedTarget === '_blank') {
            relTokens.add('noopener');
            relTokens.add('noreferrer');
        }
    }

    return {
        target: resolvedTarget,
        rel: relTokens.size ? Array.from(relTokens).join(' ') : undefined,
    };
}

export function isPartnerChip(chip: PartnerChip): chip is PartnerChipObject {
    return !!chip && typeof chip === 'object' && !React.isValidElement(chip) && 'label' in chip;
}

export function getPartnerChipKey(chip: PartnerChip, index: number) {
    if (isPartnerChip(chip)) return chip.id || getReadablePartnerText(chip.label) || index;
    return getReadablePartnerText(chip) || index;
}

export function getPartnerSchemaTypeUrl(schemaType?: PartnerSchemaType) {
    if (!schemaType) return 'https://schema.org/Organization';
    if (schemaType.startsWith('http://') || schemaType.startsWith('https://')) return schemaType;
    return `https://schema.org/${schemaType}`;
}

export function renderPartnerIcon({
    icon,
    iconToken,
    iconComponent,
    size = 16,
    className = 'partner-icon',
    cssVariable = '--partner-icon-size',
}: {
    icon?: PartnerIcon;
    iconToken?: PartnerIconToken;
    iconComponent?: PartnerIconRenderer;
    size?: number;
    className?: string;
    cssVariable?: string;
}) {
    if (icon === false) return null;

    const style = { [cssVariable]: `${size}px` } as CSSProperties;

    if (typeof icon === 'string' && icon in partnerIconRegistry) {
        const Icon = partnerIconRegistry[icon as PartnerIconToken];
        return (
            <span className={className} style={style} aria-hidden="true">
                <Icon size={size} strokeWidth={2.35} aria-hidden="true" />
            </span>
        );
    }

    if (icon && React.isValidElement(icon)) {
        return (
            <span className={className} style={style} aria-hidden="true">
                {icon}
            </span>
        );
    }

    if (icon && typeof icon === 'function') {
        const IconComponent = icon as PartnerIconRenderer;
        return (
            <span className={className} style={style} aria-hidden="true">
                <IconComponent aria-hidden={true} focusable={false} />
            </span>
        );
    }

    if (iconComponent) {
        const IconComponent = iconComponent;
        return (
            <span className={className} style={style} aria-hidden="true">
                <IconComponent aria-hidden={true} focusable={false} />
            </span>
        );
    }

    if (!iconToken) return null;

    const Icon = partnerIconRegistry[iconToken];
    return (
        <span className={className} style={style} aria-hidden="true">
            <Icon size={size} strokeWidth={2.35} aria-hidden="true" />
        </span>
    );
}

export function safePartnerJsonLd(value: unknown) {
    return JSON.stringify(value).replace(/</g, '\\u003c');
}
